import { Route, Routes } from 'react-router-dom';
import { PERMISSIONS } from '@oxxean/types';
import { RequirePermission } from '../components/RequirePermission';
import { RedirectIfAuthenticated, RequireAuth } from './guards';
import { AppLayout } from '../features/layout/AppLayout';
import { LoginPage } from '../features/auth/LoginPage';
import { ChangePasswordPage } from '../features/auth/ChangePasswordPage';
import { ForgotPasswordPage } from '../features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '../features/auth/ResetPasswordPage';
import { HomePage } from '../features/home/HomePage';
import { ExecutiveDashboardPage } from '../features/dashboard/ExecutiveDashboardPage';
import { IndicatorModulePage } from '../features/indicators/IndicatorModulePage';
import {
  ABSENCE_MODULE,
  ATTENDANCE_MODULE,
  DOCUMENTS_MODULE,
  HEADCOUNT_MODULE,
  LATENESS_MODULE,
  MOVEMENTS_MODULE,
  OVERTIME_MODULE,
  TURNOVER_MODULE,
  VACATION_MODULE,
} from '../features/indicators/module-configs';
import { LaborRelationsPage } from '../features/manual/LaborRelationsPage';
import { InspectionsPage } from '../features/manual/InspectionsPage';
import { FindingsPage } from '../features/manual/FindingsPage';
import { DiversityPage } from '../features/indicators/DiversityPage';
import { SyncPage } from '../features/sync/SyncPage';
import { ShiftsPage } from '../features/sync/ShiftsPage';
import { UnidentifiedMarksPage } from '../features/sync/UnidentifiedMarksPage';
import { UsersPage } from '../features/admin/UsersPage';
import { RolesPage } from '../features/admin/RolesPage';
import { AuditPage } from '../features/admin/AuditPage';
import { SettingsPage } from '../features/admin/SettingsPage';
import { NotFoundPage } from '../features/misc/NotFoundPage';

/**
 * Enrutado.
 *
 * Cada ruta privada va envuelta en `<RequirePermission>` con el mismo permiso que
 * declara su entrada de menú. El menú puede ocultar una entrada, pero es esta
 * guarda —y sobre todo el endpoint— la que impide el acceso.
 */
export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      {/* Públicas */}
      <Route
        path="/login"
        element={
          <RedirectIfAuthenticated>
            <LoginPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/recuperar-password"
        element={
          <RedirectIfAuthenticated>
            <ForgotPasswordPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/restablecer-password"
        element={
          <RedirectIfAuthenticated>
            <ResetPasswordPage />
          </RedirectIfAuthenticated>
        }
      />

      {/* Cambio de contraseña: fuera del layout privado, porque puede ser obligatorio */}
      <Route
        path="/cambiar-password"
        element={
          <RequireAuth>
            <ChangePasswordPage />
          </RequireAuth>
        }
      />

      {/* Privadas */}
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<HomePage />} />

        <Route
          path="/dashboard-ejecutivo"
          element={
            <RequirePermission permission={PERMISSIONS.DASHBOARD_EXECUTIVE_VIEW}>
              <ExecutiveDashboardPage />
            </RequirePermission>
          }
        />

        <Route
          path="/dotacion"
          element={
            <RequirePermission permission={HEADCOUNT_MODULE.permission}>
              <IndicatorModulePage config={HEADCOUNT_MODULE} />
            </RequirePermission>
          }
        />
        <Route
          path="/ingresos-egresos"
          element={
            <RequirePermission permission={MOVEMENTS_MODULE.permission}>
              <IndicatorModulePage config={MOVEMENTS_MODULE} />
            </RequirePermission>
          }
        />
        <Route
          path="/rotacion"
          element={
            <RequirePermission permission={TURNOVER_MODULE.permission}>
              <IndicatorModulePage config={TURNOVER_MODULE} />
            </RequirePermission>
          }
        />
        <Route
          path="/asistencia"
          element={
            <RequirePermission permission={ATTENDANCE_MODULE.permission}>
              <IndicatorModulePage config={ATTENDANCE_MODULE} />
            </RequirePermission>
          }
        />
        <Route
          path="/ausentismo"
          element={
            <RequirePermission permission={ABSENCE_MODULE.permission}>
              <IndicatorModulePage config={ABSENCE_MODULE} />
            </RequirePermission>
          }
        />
        <Route
          path="/atrasos"
          element={
            <RequirePermission permission={LATENESS_MODULE.permission}>
              <IndicatorModulePage config={LATENESS_MODULE} />
            </RequirePermission>
          }
        />
        <Route
          path="/horas-extra"
          element={
            <RequirePermission permission={OVERTIME_MODULE.permission}>
              <IndicatorModulePage config={OVERTIME_MODULE} />
            </RequirePermission>
          }
        />
        <Route
          path="/vacaciones"
          element={
            <RequirePermission permission={VACATION_MODULE.permission}>
              <IndicatorModulePage config={VACATION_MODULE} />
            </RequirePermission>
          }
        />
        <Route
          path="/documentacion-laboral"
          element={
            <RequirePermission permission={DOCUMENTS_MODULE.permission}>
              <IndicatorModulePage config={DOCUMENTS_MODULE} />
            </RequirePermission>
          }
        />

        <Route
          path="/relaciones-laborales"
          element={
            <RequirePermission permission={PERMISSIONS.LABOR_RELATIONS_VIEW}>
              <LaborRelationsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/fiscalizaciones"
          element={
            <RequirePermission permission={PERMISSIONS.INSPECTIONS_VIEW}>
              <InspectionsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/hallazgos"
          element={
            <RequirePermission permission={PERMISSIONS.FINDINGS_VIEW}>
              <FindingsPage />
            </RequirePermission>
          }
        />

        <Route
          path="/diversidad"
          element={
            <RequirePermission permission={PERMISSIONS.HEADCOUNT_VIEW}>
              <DiversityPage />
            </RequirePermission>
          }
        />

        <Route
          path="/sincronizaciones"
          element={
            <RequirePermission permission={PERMISSIONS.SYNC_VIEW}>
              <SyncPage />
            </RequirePermission>
          }
        />

        <Route
          path="/turnos"
          element={
            <RequirePermission permission={PERMISSIONS.ATTENDANCE_VIEW}>
              <ShiftsPage />
            </RequirePermission>
          }
        />

        <Route
          path="/marcaciones-sin-identificar"
          element={
            <RequirePermission permission={PERMISSIONS.ATTENDANCE_VIEW}>
              <UnidentifiedMarksPage />
            </RequirePermission>
          }
        />

        <Route
          path="/usuarios"
          element={
            <RequirePermission permission={PERMISSIONS.USERS_VIEW}>
              <UsersPage />
            </RequirePermission>
          }
        />
        <Route
          path="/roles-permisos"
          element={
            <RequirePermission permission={PERMISSIONS.ROLES_VIEW}>
              <RolesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/auditoria"
          element={
            <RequirePermission permission={PERMISSIONS.AUDIT_VIEW}>
              <AuditPage />
            </RequirePermission>
          }
        />
        <Route
          path="/configuracion"
          element={
            <RequirePermission permission={PERMISSIONS.SETTINGS_VIEW}>
              <SettingsPage />
            </RequirePermission>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
